import {useEffect, useRef, useState} from 'react'
import {useAuth} from '../modules/Auth/shared/state'
import {useDeactivationState} from "@src/modules/Auth/shared/DeactivationWrapper"


export function useApiWithSiteId<T = any>(
  apiFetcher,
  // 'false' will not trigger a call
  opts?: false | Parameters<typeof apiFetcher>[0],
): {
  call: () => void
  data?: T
  error?: undefined | string
  isCalling: boolean
  reset: () => void
} {
  const local = useRef({ call: () => undefined, prevOpts: null })
  const [data, setData] = useState<ReturnType<typeof apiFetcher>>(undefined)
  const [error, setError] = useState<undefined | string>(undefined)
  const {setDeactivatedMsg} = useDeactivationState()
  const { currentSite } = useAuth()
  const siteId = currentSite ? currentSite.id : ''
  const [isCalling, setIsCalling] = useState(false)
  const call = () => {
    setIsCalling(true)
    const innerCall = (typeof opts === 'object') ? apiFetcher({...opts, siteId}) : apiFetcher(opts, {siteId})
    innerCall
      .then((res) => {
        setData(res.data)
      })
      .catch((err) => {
        console.warn('useApiWithSiteId() error', err)
        if (err?.response?.status === 402) setDeactivatedMsg(err?.response?.data?.message || "Account Deactivated")
        setError(err?.response?.data?.message || 'API error')
      })
      .finally(() => setIsCalling(false))
  }

  local.current.call = call

  useEffect(() => {
    if (opts !== false) {
      const { prevOpts } = local.current

      if (typeof opts !== 'object' && prevOpts !== opts) {
        local.current.prevOpts = opts
        local.current.call()
      } else if (
        typeof opts === 'object' &&
        JSON.stringify(prevOpts) !== JSON.stringify(opts)
      ) {
        local.current.prevOpts = opts
        local.current.call()
      }
    }
  }, [opts])

  const reset = () => {
    setData(undefined)
    setError(undefined)
    setIsCalling(false)
  }

  return {
    call,
    data,
    error,
    isCalling,
    reset,
  }
}
